import type { DescMessage, DescMethodUnary } from "@bufbuild/protobuf";

import {
  accessLogStatusColor,
  buildAccessLogColorOverrides,
  sortAccessLogSeries,
} from "@/components/access-logs/accessLogChartStyle";
import type { SliceColorFn } from "@/components/aggregations/aggregatePartToWhole";
import type { ChartColorOverrides } from "@/components/charts/AggregatedChart";
import type { SeriesNullablePoints } from "@/components/charts/GRPCMetricChartImpl";
import type { IconName } from "@/components/icons/Icon";
import {
  KUBE_EVENT_SEVERITY_COLORS,
  kubeEventSeverityColor,
  sortKubeEventSeries,
} from "@/components/kube-events/kubeEventChartStyle";
import type { FacetQueryMethod } from "@/components/facets/facets.types";
import { addExclusionToQuery, buildFacetGroupClause } from "@/components/logs/logFacets.utils";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@/constants/stable-empty";
import {
  getAccessLogFacetValues,
  getAccessLogFacets,
  getAccessLogStat,
  getLogFacetValues,
  getLogFacets,
  getLogStat,
  searchAccessLogEntriesAggregated,
  searchLogEntriesAggregated,
} from "@/gen_src/proto/chalk/server/v1/log-LogSearchService_connectquery";
import {
  getKubeEventFacetValues,
  getKubeEventFacets,
  getKubeEventStat,
  listKubeEventsAggregated,
} from "@/gen_src/proto/chalk/server/v1/kube_events-KubeEventsService_connectquery";
import type { StatisticResult } from "@/gen_src/proto/chalk/server/v1/chart_pb";
import type { AggregationFunction } from "@/gen_src/proto/chalk/searchaggregates/v1/aggregation_pb";
import {
  LOG_SEVERITY_CHART_COLOR_OVERRIDES,
  logSeverityColor,
  sortLogSeries,
} from "@/utils/logs-utils";
import type { TimeRangeSelection } from "@/components/interactive/SharedTimeRange.types";
import { DynamicQueryParam } from "@/constants/query-params";
import { Routes } from "@/utils/routes";
import { setTimeRangeSelectionParams } from "@/utils/time-range-params";

/**
 * What a widget needs to know about the surface it queries. Data only: components are chosen at the
 * render site, since a hook cannot be dispatched from a table. `id` is the persisted
 * `DashboardSourceQuery.data_source`, so it must match the server's allowlist in dashboard/validate.go.
 */
export type WidgetSourceID = "logs" | "access_logs" | "kube_events";

export const LOGS_DATA_SOURCE = "logs" satisfies WidgetSourceID;
export const ACCESS_LOGS_DATA_SOURCE = "access_logs" satisfies WidgetSourceID;
export const KUBE_EVENTS_DATA_SOURCE = "kube_events" satisfies WidgetSourceID;

export interface WidgetSourceSpec {
  id: WidgetSourceID;
  label: string;
  icon: IconName;
  /** Facet catalog and value suggestions for the query bar and the aggregation builder. */
  facetsQuery: FacetQueryMethod;
  facetValuesQuery: FacetQueryMethod;
  buildValueQuery: (key: string, value: string) => string;
  /** Appends a NOT clause excluding a group tuple, with the surface's own key/value escaping. */
  addExclusionToQuery: (query: string, predicates: { key: string; value: string }[]) => string;
  /** Recent-search scope, shared with the surface's own explorer page. */
  storageScope: string;
  searchPlaceholder: string;
  /** Bucketed time series. Loosely typed like AggregatedChart's own prop. */
  chartQuery: DescMethodUnary<DescMessage, DescMessage>;
  /** Loose for the same reason FacetQueryMethod is: a union of descriptors defeats hook inference. */
  statQuery: DescMethodUnary<DescMessage, DescMessage>;
  /** The dimension whose values carry meaning-bearing colours (severity, status code). */
  naturalFacet: string;
  naturalColorOverrides: ChartColorOverrides;
  naturalSeriesTransform: (series: SeriesNullablePoints[]) => SeriesNullablePoints[];
  /** The same colours by value rather than by series, for a part-to-whole's mark per group. */
  naturalSliceColor: SliceColorFn;
  /** What one row is called, for the measure menu's subheader over Count. */
  rowNoun: string;
  /** Fallback widget titles for a raw row list, which has nothing but its rendering to describe. */
  listChartName: string;
  listTableName: string;
  /** The surface's own explorer page, without query params; `widgetSourceSearchLink` adds those. */
  explorerPath: (projectId: string, environmentId: string) => string;
}

export const WIDGET_SOURCES: Record<WidgetSourceID, WidgetSourceSpec> = {
  logs: {
    id: LOGS_DATA_SOURCE,
    label: "Logs",
    icon: "list",
    facetsQuery: getLogFacets,
    facetValuesQuery: getLogFacetValues,
    buildValueQuery: (key, value) => buildFacetGroupClause(key, [value]),
    addExclusionToQuery,
    storageScope: "logs",
    searchPlaceholder: "Search logs",
    chartQuery: searchLogEntriesAggregated,
    statQuery: getLogStat,
    naturalFacet: "severity",
    naturalColorOverrides: LOG_SEVERITY_CHART_COLOR_OVERRIDES,
    naturalSeriesTransform: sortLogSeries,
    naturalSliceColor: logSeverityColor,
    rowNoun: "Log lines",
    listChartName: "Log Chart",
    listTableName: "Log Table",
    explorerPath: (projectId, environmentId) =>
      Routes.project.environment.logs.index(projectId, environmentId),
  },
  access_logs: {
    id: ACCESS_LOGS_DATA_SOURCE,
    label: "Access Logs",
    // Matches the sidebar's access-logs entry.
    icon: "trafficCone",
    facetsQuery: getAccessLogFacets,
    facetValuesQuery: getAccessLogFacetValues,
    buildValueQuery: (key, value) => buildFacetGroupClause(key, [value]),
    addExclusionToQuery: addExclusionToQuery,
    storageScope: "access-logs",
    searchPlaceholder: "Search access logs",
    chartQuery: searchAccessLogEntriesAggregated,
    statQuery: getAccessLogStat,
    naturalFacet: "status_code",
    naturalColorOverrides: buildAccessLogColorOverrides,
    naturalSeriesTransform: sortAccessLogSeries,
    naturalSliceColor: accessLogStatusColor,
    rowNoun: "Requests",
    listChartName: "Access Log Chart",
    listTableName: "Access Log Table",
    explorerPath: (projectId, environmentId) =>
      Routes.project.environment.logs.access(projectId, environmentId),
  },
  kube_events: {
    id: KUBE_EVENTS_DATA_SOURCE,
    label: "Kube Events",
    // Matches the sidebar's kube-events entry.
    icon: "audioLines",
    facetsQuery: getKubeEventFacets,
    facetValuesQuery: getKubeEventFacetValues,
    buildValueQuery: (key, value) => buildFacetGroupClause(key, [value]),
    // Kube events share the log grammar, so they share its escaping (see KubeEventsSearch).
    addExclusionToQuery,
    storageScope: "kube-events",
    searchPlaceholder: "Search kube events",
    chartQuery: listKubeEventsAggregated,
    statQuery: getKubeEventStat,
    naturalFacet: "severity",
    naturalColorOverrides: KUBE_EVENT_SEVERITY_COLORS,
    naturalSeriesTransform: sortKubeEventSeries,
    naturalSliceColor: kubeEventSeverityColor,
    rowNoun: "Events",
    listChartName: "Kube Event Chart",
    listTableName: "Kube Event Table",
    explorerPath: (projectId, environmentId) =>
      Routes.project.environment.kubeEvents(projectId, environmentId),
  },
};

export const widgetSourceOf = (dataSource: string): WidgetSourceSpec | null =>
  dataSource in WIDGET_SOURCES ? WIDGET_SOURCES[dataSource as WidgetSourceID] : null;

/** Everything the explorer needs to reproduce what a host is showing. */
export interface WidgetSourceView {
  /** The faceted filter, without any aggregation clause. */
  query: string;
  /** The aggregation and its visualization, already encoded by `encodeAggregationState`. */
  aggregation?: string | null;
  /** The host's window, kind intact: a lookback stays relative and keeps following "now". */
  selection?: TimeRangeSelection;
}

/** The link out to a source's explorer page, carrying the whole view. Null until the route ids
 * resolve, so a caller renders nothing rather than a dead href. */
export const widgetSourceSearchLink = (
  source: WidgetSourceSpec,
  view: WidgetSourceView,
  projectId: string | undefined,
  environmentId: string | undefined,
): { href: string; label: string } | null => {
  if (projectId == null || environmentId == null) return null;
  const params = new URLSearchParams();
  if (view.query) params.set(DynamicQueryParam.QUERY_STRING, view.query);
  if (view.aggregation) params.set(DynamicQueryParam.LOG_AGGREGATION, view.aggregation);
  if (view.selection != null) setTimeRangeSelectionParams(params, view.selection);
  const query = params.toString();
  return {
    href: `${source.explorerPath(projectId, environmentId)}${query ? `?${query}` : ""}`,
    label: `Open in ${source.label}`,
  };
};

/** Every source's stat RPC answers `{ result }`; the read is structural because the method is loose. */
export const statResultOf = (data: unknown): StatisticResult | undefined =>
  (data as { result?: StatisticResult } | undefined)?.result;

/**
 * The cross-surface facet shape. LogFacet, KubeEventFacet and SpanFacet agree on these fields, and
 * their per-message facetType enums share numeric values — the same reasoning ProtoFacet documents.
 */
export interface TileFacet {
  path: string;
  name: string;
  facetType: number;
  groupable: boolean;
  supportedAggregations: AggregationFunction[];
}

/** Facet catalogs are read structurally for the same reason the RPCs are typed loosely. */
export const facetsOf = (data: unknown): readonly TileFacet[] =>
  (data as { facets?: TileFacet[] } | undefined)?.facets ?? EMPTY_ARRAY;

export interface WidgetSeriesStyle {
  colorOverrides: ChartColorOverrides;
  // Undefined rather than an identity function: the chart memoizes on this.
  seriesTransform: ((series: SeriesNullablePoints[]) => SeriesNullablePoints[]) | undefined;
}

/**
 * The surface's own colours apply when the chart is ungrouped or grouped solely by its natural
 * dimension; any other breakdown uses chart defaults, whose palette distinguishes arbitrary groups.
 * Same rule every explorer page applies, so a widget and its page draw alike.
 */
/** The natural breakdown's own colours, by value. Unlike a series style, an ungrouped query does not
 *  qualify: its one mark stands for everything, so there is no value to colour by. */
export const widgetSliceColor = (
  spec: WidgetSourceSpec,
  groupBy: readonly string[],
): SliceColorFn | undefined =>
  groupBy.length === 1 && groupBy[0] === spec.naturalFacet ? spec.naturalSliceColor : undefined;

export const widgetSeriesStyle = (
  spec: WidgetSourceSpec,
  groupBy: readonly string[],
): WidgetSeriesStyle => {
  const isNatural =
    groupBy.length === 0 || (groupBy.length === 1 && groupBy[0] === spec.naturalFacet);
  return isNatural
    ? { colorOverrides: spec.naturalColorOverrides, seriesTransform: spec.naturalSeriesTransform }
    : { colorOverrides: EMPTY_OBJECT, seriesTransform: undefined };
};
